import CustomDialog from "components/Atoms/CustomDialog"
import StaticSelector from "components/Atoms/StaticSelector"
import StringInput from "components/Atoms/StringInput"
import ButtonMutation from "components/Molecules/ButtonMutation"
import FileUpload from "components/Molecules/FileUpload"
import FormController from "components/Molecules/FormController"
import { ADD_UPDATE_ORDER_TRANSACTIONS } from "graphql/order/order.mutations"
import { getErrors } from "hooks/useErrors"
import { cloneDeep, set, sumBy } from "lodash-es"
import CustomButton from "components/Atoms/CustomButton"
import transanctionModel from "models/transanction.model"
import React, { useMemo, useState } from "react"
import { ErrorToast, SuccessToast } from "utils/AppToaster"
import { inr } from "utils/formatters.utils"
import { PAYMENT_METHODS } from "data/static.items"
// Desktop: one line per payment under a shared header. Mobile: each payment is a labelled card
const ROW_GRID = "md:grid-cols-[28px_repeat(5,minmax(0,1fr))_48px]"

const Field = ({ label, children }) => (
  <div className="min-w-0">
    <div className="md:hidden text-xs font-600 mb-1">{label}</div>
    {children}
  </div>
)
const TYPES = [
  { id: "CAP", name: "Credit" },
  { id: "REF", name: "Debit" },
]

// New payments default to Credit
const newRow = () => ({ status: TYPES[0] })

const AddTransanction = ({ isOpen, onClose, order }) => {
  const outstanding = order?.outstandingAmount
  const orderId = order?.id
  const [state, setState] = useState<any>({ transactions: [newRow()] })
  const rows: any[] = state?.transactions || []

  const parsedVariables = useMemo(() => {
    return {
      orderId,
      transactions: rows.map(row => ({
        amount: row?.amount ? Number(row.amount) : row?.amount,
        referenceId: row?.referenceId,
        status: row?.status?.id,
        paymentMethod: row?.paymentMethod?.id,
        attachment: row?.attachment
          ? { url: row.attachment, mediaType: "PHT" }
          : undefined,
      })),
    }
  }, [rows, orderId])

  function handleChange(path, value) {
    const stateCopy = cloneDeep(state)
    set(stateCopy, path, value)
    setState(stateCopy)
  }

  function addRow() {
    setState({ ...state, transactions: [...rows, newRow()] })
  }

  function removeRow(index) {
    if (rows.length === 1) {
      return setState({ ...state, transactions: [newRow()] })
    }
    setState({ ...state, transactions: rows.filter((_, i) => i !== index) })
  }

  function reset() {
    setState({ transactions: [newRow()] })
  }

  return (
    <>
      <CustomDialog
        icon="th-list"
        light
        width="min(96vw, 1000px)"
        isOpen={isOpen}
        onClose={onClose}
        footerRightButton={
          <ButtonMutation
            onError={() =>
              ErrorToast("Unable to add transanctions, please try again!")
            }
            onCompleted={({ addUpdateOrderTransactions: auot }) => {
              const count = auot?.orderTransactions?.length || 0
              SuccessToast(
                `Successfully added ${count} transaction${
                  count === 1 ? "" : "s"
                } for order ${order?.trackingCode}`
              )
              reset()
              onClose()
            }}
            validate={params => {
              for (let i = 0; i < params.transactions.length; i++) {
                const t = params.transactions[i]
                const errors = getErrors({
                  state: { ...t, orderId: params.orderId },
                  schema: transanctionModel,
                  abortEarly: true,
                })
                if (errors?.latest) {
                  return ErrorToast(`Payment ${i + 1}: ${errors?.latest}`)
                }
              }
              const credit = sumBy(
                (params.transactions as any[]).filter(t => t.status === "CAP"),
                (t: any) => Number(t.amount) || 0
              )
              if (credit > outstanding) {
                return ErrorToast(
                  `Total payment amount : ${inr(
                    credit
                  )} exceeds the outstanding : ${inr(
                    outstanding
                  )}, payment amount cannot be greater than the outstanding`
                )
              }
              return true
            }}
            icon="confirm"
            text="Transactions"
            mutation={ADD_UPDATE_ORDER_TRANSACTIONS}
            variables={parsedVariables}
          />
        }
        title={
          <span>
            <span className="vt-muted"> Order - </span>
            {order?.trackingCode}
          </span>
        }
      >
        <FormController source={state} onChange={handleChange}>
          <div className="mb-3 flex flex-wrap gap-x-6">
            <span>
              Amount <span className="vt-success">{inr(order?.totalPaid)}</span>
              / <span className="vt-danger">{inr(order?.totalAmount)}</span>{" "}
            </span>
            <span>Outstanding {inr(outstanding)}</span>
          </div>
          <div
            className={`hidden md:grid gap-4 text-xs font-600 mb-1 ${ROW_GRID}`}
          >
            <div>#</div>
            <div>Payment Method *</div>
            <div>Amount *</div>
            <div>Transaction Type *</div>
            <div>Reference ID</div>
            <div>Photo</div>
            <div>Action</div>
          </div>
          {rows.map((_, i) => (
            <div
              key={i}
              className={`grid grid-cols-2 gap-x-4 gap-y-3 md:items-center mb-3 p-3 border rounded-lg md:p-0 md:border-0 md:rounded-none md:mb-2 ${ROW_GRID}`}
            >
              <div className="col-span-2 md:col-span-1 flex justify-between items-center md:pb-3">
                <span className="font-600">{i + 1}.</span>
                <CustomButton
                  className="md:hidden"
                  minimal
                  icon="trash"
                  intent="danger"
                  onClick={() => removeRow(i)}
                />
              </div>
              <Field label="Payment Method *">
                <StaticSelector
                  absolute
                  items={PAYMENT_METHODS}
                  path={`transactions[${i}].paymentMethod`}
                />
              </Field>
              <Field label="Amount *">
                <StringInput
                  big={false}
                  leftIcon="rupee"
                  type="number"
                  placeholder="Enter"
                  path={`transactions[${i}].amount`}
                />
              </Field>
              <Field label="Transaction Type *">
                <StaticSelector
                  absolute
                  items={TYPES}
                  path={`transactions[${i}].status`}
                />
              </Field>
              <Field label="Reference ID">
                <StringInput
                  big={false}
                  placeholder="Enter"
                  path={`transactions[${i}].referenceId`}
                />
              </Field>
              <div className="col-span-2 md:col-span-1 min-w-0">
                <div className="md:hidden text-xs font-600 mb-1">Photo</div>
                <FileUpload
                  foldername="payments"
                  path={`transactions[${i}].attachment`}
                />
              </div>
              <div className="hidden md:block">
                <CustomButton
                  minimal
                  icon="trash"
                  intent="danger"
                  onClick={() => removeRow(i)}
                />
              </div>
            </div>
          ))}
          <div className="w-full flex justify-center text-xs text-gray-500">
            <CustomButton
              minimal
              intent="primary"
              icon="plus"
              text="Add Another Payment"
              onClick={addRow}
            />
          </div>
        </FormController>
      </CustomDialog>
    </>
  )
}

export default AddTransanction
